"use client";
import React from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import EastIcon from "@mui/icons-material/East";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { IMAGES } from "@/constants/images";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
const cards = [
  {
    objectionKey: "objectionsSection.c1.objection",
    responseKey: "objectionsSection.c1.response",
    nameKey: "objectionsSection.c1.name",
    roleKey: "objectionsSection.c1.role",
  },
  {
    objectionKey: "objectionsSection.c2.objection",
    responseKey: "objectionsSection.c2.response",
    nameKey: "objectionsSection.c2.name",
    roleKey: "objectionsSection.c2.role",
  },
  {
    objectionKey: "objectionsSection.c3.objection",
    responseKey: "objectionsSection.c3.response",
    nameKey: "objectionsSection.c3.name",
    roleKey: "objectionsSection.c3.role",
  },
  {
    objectionKey: "objectionsSection.c4.objection",
    responseKey: "objectionsSection.c4.response",
    nameKey: "objectionsSection.c4.name",
    roleKey: "objectionsSection.c4.role",
  },
];

const ObjectionsSection = () => {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const isArabic = i18n.language === "ar";

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        py: { xs: 4, sm: 6, md: 12 },
        direction: isArabic ? "rtl" : "ltr",
        overflowX: "hidden",
        width: "100%",
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          px: { xs: 2, sm: 3, md: 6, lg: 8 },
          overflowX: "hidden",
        }}
      >
        {/* ── HEADING ── */}
        <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4, md: 5 } }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: "var(--font-plusjakarta)",
              fontWeight: "bold",
              fontSize: { xs: "1.6rem", sm: "2.2rem", md: "3.5rem" },
              color: "#101828",
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              mb: { xs: 1.5, sm: 2, md: 2.5 },
              wordBreak: "break-word",
            }}
          >
            {t("objectionsSection.heading1")}
            <br />
            {t("objectionsSection.heading2")}
          </Typography>

          {/* Sub badge */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              mb: { xs: 3, sm: 4, md: 6 },
            }}
          >
            <Typography
              sx={{
                fontFamily: "var(--font-outfit), sans-serif",
                fontSize: { xs: "0.68rem", sm: "0.75rem", md: "1rem" },
                fontWeight: "bold",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#99A1AF",
              }}
            >
              {t("objectionsSection.subBadge")}
            </Typography>
          </Box>
        </Box>

        {/* ── CARDS GRID ── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              lg: "repeat(4, 1fr)",
            },
            gap: { xs: 1.5, sm: 1.5 },
            mb: { xs: 4, sm: 5, md: 9 },
            width: "100%",
            minWidth: 0,
          }}
        >
          {cards.map((card, i) => (
            /* Two-tone border card */
            <Box
              key={i}
              sx={{
                borderRadius: { xs: "14px", md: "16px" },
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
                minWidth: 0,
                boxSizing: "border-box",
                wordBreak: "break-word",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
                },
              }}
            >
              {/* Inner wrapper — no extra border wrapper needed */}
              <Box
                sx={{
                  borderRadius: { xs: "14px", md: "16px" },
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  flex: 1,
                }}
              >
                {/* ── TOP: Objection (red tint) ── */}
                <Box
                  sx={{
                    backgroundColor: "#fef2f2",
                    pt: { xs: 2.5, md: 3 },
                    px: { xs: 2.5, md: 3 },
                    pb: 0,
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #fecaca",
                    borderBottom: "none",
                    borderRadius: "14px 14px 0 0",
                  }}
                >
                  {/* ADS_CROSS image icon */}
                  <Box
                    sx={{
                      width: { xs: 30, md: 30 },
                      height: { xs: 30, md: 30 },
                      mb: { xs: 1.5, md: 2 },
                      flexShrink: 0,
                      position: "relative",
                    }}
                  >
                    <Image
                      src={IMAGES.ADS_CROSS}
                      alt="objection"
                      fill
                      style={{ objectFit: "contain" }}
                    />
                  </Box>

                  {/* Objection Text */}
                  <Typography
                    sx={{
                      fontFamily: "var(--font-outfit)",
                      fontWeight: 800,
                      fontSize: { xs: "0.85rem", sm: "0.88rem", md: "1rem" },
                      color: "#101828",
                      lineHeight: 1.45,
                      mb: { xs: 2, md: 2.5 },
                    }}
                  >
                    {t(card.objectionKey)}
                  </Typography>

                  {/* Red divider — sits on the bottom border of red section */}
                  <Box
                    sx={{
                      width: 28,
                      height: "3px",
                      backgroundColor: "#ef4444",
                      borderRadius: "2px 2px 0 0",
                    }}
                  />
                </Box>

                {/* ── BOTTOM: Response (green tint) ── */}
                <Box
                  sx={{
                    backgroundColor: "#f0fdf4",
                    pt: { xs: 2.5, md: 3 },
                    px: { xs: 2.5, md: 3 },
                    pb: 0,
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    border: "1px solid #bbf7d0",
                    borderTop: "none",
                    borderRadius: "0 0 14px 14px",
                  }}
                >
                  {/* ADS_TICK image icon */}
                  <Box
                    sx={{
                      width: { xs: 30, md: 30 },
                      height: { xs: 30, md: 30 },
                      mb: { xs: 1.5, md: 2 },
                      flexShrink: 0,
                      position: "relative",
                    }}
                  >
                    <Image
                      src={IMAGES.ADS_TICK}
                      alt="response"
                      fill
                      style={{ objectFit: "contain" }}
                    />
                  </Box>

                  {/* Response Text */}
                  <Typography
                    sx={{
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "0.8rem", sm: "0.83rem", md: "0.9rem" },
                      color: "#4A5565",
                      lineHeight: 1.7,
                      mb: { xs: 2, md: 2.5 },
                    }}
                  >
                    {t(card.responseKey)}
                  </Typography>

                  {/* Green divider — sits on the bottom border of the card */}
                  <Box
                    sx={{
                      width: 28,
                      height: "3px",
                      backgroundColor: "#22c55e",
                      borderRadius: "2px 2px 0 0",
                      mt: "auto",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── BOTTOM CTA SECTION ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            textAlign: { xs: "center", sm: "left" },
            gap: { xs: 2.5, sm: 3 },
            borderTop: "1px solid #e2e8f0",
            pt: { xs: 3, sm: 4, md: 5 },
            minWidth: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: "var(--font-plusjakarta), sans-serif",
                fontWeight: 800,
                fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                color: "#0f172a",
                mb: 0.5,
                wordBreak: "break-word",
              }}
            >
              {t("objectionsSection.footerHeading")}
            </Typography>
            <Typography
              sx={{
                fontFamily: "var(--font-outfit), sans-serif",
                fontSize: { xs: "0.82rem", sm: "0.85rem", md: "0.88rem" },
                color: "#64748b",
                wordBreak: "break-word",
              }}
            >
              {t("objectionsSection.footerSub")}
            </Typography>
          </Box>

          {/* CTA Button */}
          <Button
            id="btn-seven"
            // onClick={() => {
            //   const el = document.getElementById("book-demo-section");
            //   if (el) el.scrollIntoView({ behavior: "smooth" });
            // }}
            onClick={() => {
              window.dispatchEvent(new Event("open-book-demo-modal"));
            }}
            endIcon={
              <ArrowForwardIcon
                sx={{
                  fontSize: "1.2rem !important",
                  ml: 0.5,
                  mr: 1.5,
                  transform: isArabic ? "rotate(180deg)" : "none",
                }}
              />
            }
            sx={{
              backgroundColor: "#0b0f19",
              color: "#fff",
              fontFamily: "var(--font-outfit), sans-serif",
              fontWeight: 700,
              fontSize: { xs: "0.85rem", md: "0.92rem" },
              px: { xs: 3, md: 3.5 },
              py: { xs: 1.4, md: 1.6 },
              borderRadius: "50px",
              textTransform: "none",
              whiteSpace: "nowrap",
              flexShrink: 0,
              alignSelf: { xs: "center", sm: "auto" },
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                backgroundColor: "#161b26",
                transform: "scale(1.05)",
              },
              "&:active": { transform: "translateY(0)" },
            }}
          >
            {t("objectionsSection.cta")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ObjectionsSection;
